import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const VIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_neon-narrative-hub/artifacts/2quhia9l_grok_video_2026-04-24-18-56-36.mp4";

const PillChoice = () => {
  const videoRef = useRef(null);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let timer;
    const onPlaying = () => {
      timer = setTimeout(() => setTextVisible(true), 400);
    };
    v.addEventListener("playing", onPlaying, { once: true });
    const fallback = setTimeout(() => setTextVisible(true), 1200);
    const p = v.play?.();
    if (p && typeof p.catch === "function") {
      p.catch(() => setTextVisible(true));
    }
    return () => {
      v.removeEventListener("playing", onPlaying);
      clearTimeout(timer);
      clearTimeout(fallback);
    };
  }, []);

  const pulse = (ev, color) => {
    const el = ev.currentTarget;
    el.style.setProperty("--pill-pulse-color", color);
    el.classList.remove("pill-pulse");
    void el.offsetWidth;
    el.classList.add("pill-pulse");
    setTimeout(() => el.classList.remove("pill-pulse"), 320);
  };

  const ease = [0.22, 1, 0.36, 1];

  return (
    <section
      id="pill-choice"
      className="relative w-full overflow-hidden snap-section snap-reveal"
      data-testid="section-pill-choice"
    >
      <div className="relative w-full h-[70vh] min-h-[560px] md:h-[90vh] md:min-h-[680px]">
        {/* Background video — single continuous file */}
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={VIDEO_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="pill-video"
        />

        {/* Base dark readability overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,10,0.72) 0%, rgba(5,5,10,0.45) 25%, rgba(5,5,10,0.45) 65%, rgba(5,5,10,0.82) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 30%, rgba(5,5,10,0.5) 100%)",
          }}
        />

        {/* Left half — cool blue tint */}
        <div
          className="absolute inset-y-0 left-0 w-1/2 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(0,110,255,0.22) 0%, rgba(0,90,220,0.15) 55%, rgba(0,0,0,0) 100%)",
          }}
          aria-hidden
        />
        {/* Right half — subtle red tint */}
        <div
          className="absolute inset-y-0 right-0 w-1/2 pointer-events-none"
          style={{
            background:
              "linear-gradient(270deg, rgba(255,40,40,0.22) 0%, rgba(220,30,30,0.15) 55%, rgba(0,0,0,0) 100%)",
          }}
          aria-hidden
        />

        {/* Edge fades for seamless section transition */}
        <div
          className="absolute top-0 left-0 right-0 h-32 pointer-events-none z-[2]"
          style={{ background: "linear-gradient(180deg, #05050A 0%, transparent 100%)" }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-[2]"
          style={{ background: "linear-gradient(0deg, #05050A 0%, transparent 100%)" }}
        />

        {/* Subtle scanlines */}
        <div
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px)",
            mixBlendMode: "overlay",
          }}
        />

        {/* Center vertical divider */}
        <div
          className="absolute top-[18%] bottom-[18%] left-1/2 -translate-x-1/2 z-[3] pointer-events-none"
          style={{ width: "1px" }}
          aria-hidden
        >
          <div
            className="w-full h-full"
            style={{
              background:
                "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.75) 50%, transparent 100%)",
              boxShadow:
                "0 0 14px rgba(120,180,255,0.55), 0 0 24px rgba(255,90,90,0.4)",
              animation: "uthPulse 3.2s ease-in-out infinite",
            }}
          />
        </div>

        {/* Split content */}
        <div className="relative z-[4] grid grid-cols-2 w-full h-full">
          {/* ---------- LEFT — BLUE PILL ---------- */}
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 18, filter: "blur(10px)" }}
            animate={
              textVisible
                ? { opacity: 1, y: 0, filter: "blur(0px)" }
                : { opacity: 0, y: 18, filter: "blur(10px)" }
            }
            transition={{ duration: 1.4, ease, delay: 0.15 }}
            className="pill-side pill-side--blue relative flex flex-col items-center justify-center text-center px-5 sm:px-8 md:px-12 bg-transparent border-0 cursor-pointer select-none"
            data-testid="pill-left"
            onClick={(e) => pulse(e, "rgba(80,140,255,0.3)")}
          >
            <span
              className="font-mono-uth uppercase mb-4 md:mb-7 tracking-[0.28em] text-[10px] md:text-xs"
              style={{
                color: "#8AB8FF",
                textShadow: "0 0 14px rgba(80,130,255,0.45)",
              }}
            >
              // Blue Pill
            </span>
            <span
              className="font-display font-black uppercase leading-[0.98] tracking-tight break-words max-w-full"
              style={{
                fontSize: "clamp(1.1rem, 4vw, 3.6rem)",
                color: "#9CC2FF",
                textShadow:
                  "0 0 22px rgba(70,130,255,0.5), 0 0 50px rgba(0,100,220,0.3), 0 2px 18px rgba(0,0,0,0.7)",
              }}
            >
              Paid
              <br />
              Propaganda.
            </span>
          </motion.button>

          {/* ---------- RIGHT — RED PILL ---------- */}
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 22 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
            transition={{ duration: 1.05, ease, delay: 0.35 }}
            className="pill-side pill-side--red relative flex flex-col items-center justify-center text-center px-5 sm:px-8 md:px-12 bg-transparent border-0 cursor-pointer select-none"
            data-testid="pill-right"
            onClick={(e) => pulse(e, "rgba(255,80,80,0.32)")}
          >
            <span
              className="font-mono-uth uppercase mb-4 md:mb-7 tracking-[0.28em] text-[10px] md:text-xs"
              style={{
                color: "#FF9C9C",
                textShadow: "0 0 14px rgba(255,80,80,0.55)",
              }}
            >
              // Red Pill
            </span>
            <span
              className="font-display font-black uppercase leading-[0.98] tracking-tight text-white break-words max-w-full"
              style={{
                fontSize: "clamp(1.15rem, 4.4vw, 3.9rem)",
                textShadow:
                  "0 0 22px rgba(255,80,80,0.6), 0 0 48px rgba(255,255,255,0.2), 0 2px 20px rgba(0,0,0,0.75)",
              }}
            >
              Unbiased
              <br />
              Truth.
            </span>
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default PillChoice;
